import type { CSSProperties, KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import type { AuthStatusResponse } from '@shared/api';
import { navigate } from '../../state/router';
import type { AuthStore } from '../../state/useAuth';
import type { ThemeStore } from '../../state/useTheme';
import { AppBrand } from '../AppBrand';
import { ChevronDownIcon } from '../icons';

/** "jane.doe@agency.com" -> "JD"; falls back gracefully for anything odd. */
export function initialsFromUsername(username: string): string {
  const local = username.split('@')[0] ?? username;
  const words = local.split(/[._+-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase();
  if (words.length === 1 && words[0]!.length >= 2) return words[0]!.slice(0, 2).toUpperCase();
  return username.slice(0, 2).toUpperCase() || '?';
}

/** "jane.doe@agency.com" -> "Jane Doe" - a display name derived from the one
 * identity string the API gives us (Cognito username), never invented data. */
export function displayNameFromUsername(username: string): string {
  const local = username.split('@')[0] ?? username;
  const words = local.split(/[._+-]+/).filter(Boolean);
  if (words.length === 0) return username;
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

/** The org name the rep is working in: the active organization when one is
 * picked, else the brand the server resolved for them. */
export function activeOrgName(status: AuthStatusResponse): string {
  const active = status.organizations.find((org) => org.id === status.activeOrganizationId);
  return active?.name ?? status.branding.appName;
}

function Glyph({ children }: { children: ReactNode }): ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

const PersonGlyph = () => (
  <Glyph>
    <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2.75 14c.6-2.6 2.7-4.1 5.25-4.1s4.65 1.5 5.25 4.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </Glyph>
);
const CardsGlyph = () => (
  <Glyph>
    <rect x="2" y="5.5" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4 3.25h8M5.5 9.5h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </Glyph>
);
const MoonGlyph = () => (
  <Glyph>
    <path d="M13.5 9.53A5.75 5.75 0 1 1 6.47 2.5a4.6 4.6 0 0 0 7.03 7.03Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </Glyph>
);
const ExitGlyph = () => (
  <Glyph>
    <path d="M6.5 2.75H4a1.25 1.25 0 0 0-1.25 1.25v8A1.25 1.25 0 0 0 4 13.25h2.5M10.5 5.25 13.25 8l-2.75 2.75M13 8H6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </Glyph>
);

interface OrgChoice {
  key: string;
  name: string;
  branding: AuthStatusResponse['branding'];
  organizationId: string | null;
  cobrand: string | null;
  selected: boolean;
}

/**
 * The account button at the foot of the sidebar and the menu it opens
 * (upward): who is signed in, the organization switch (only when there is
 * more than one to pick — most reps have exactly one), settings (account,
 * org and credits), dark mode, the credits admin for its one admin, and log out.
 *
 * The org switch used to be the brand picker in the sidebar's top-left;
 * that spot is now the Proposal wordmark. It is one list combining the
 * rep's own organizations and, for OrgsAdmin users only, the cobrand
 * preview (`cobrandOptions`) — nobody needs the two as separate menus.
 */
export function AccountMenu({
  auth,
  themeStore,
  variant,
}: {
  auth: AuthStore;
  themeStore: Pick<ThemeStore, 'theme' | 'toggleTheme'>;
  variant: 'full' | 'rail';
}): ReactElement | null {
  const status = auth.status;
  const [open, setOpen] = useState(false);
  // The collapsed rail is 60px wide and clips overflow, so its menu is
  // pinned to the viewport beside the avatar instead.
  const [railAnchor, setRailAnchor] = useState<CSSProperties | undefined>(undefined);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    // Focus the first item so the menu is keyboard-usable straight away.
    panel.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  if (!status?.signedIn || !status.username) return null;
  const username = status.username;
  const { theme, toggleTheme } = themeStore;

  const close = (returnFocus = false) => {
    setOpen(false);
    if (returnFocus) trigger.current?.focus();
  };
  const go = (path: string) => {
    close();
    navigate(path);
  };

  const toggle = () => {
    if (!open && variant === 'rail' && trigger.current) {
      const rect = trigger.current.getBoundingClientRect();
      setRailAnchor({ position: 'fixed', left: rect.right + 10, bottom: window.innerHeight - rect.bottom });
    }
    setOpen((value) => !value);
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' || event.key === 'Tab') {
      if (event.key === 'Escape') event.preventDefault();
      close(true);
      return;
    }
    const items = Array.from(panel.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)') ?? []);
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = index < 0 || index === items.length - 1 ? 0 : index + 1;
    if (event.key === 'ArrowUp') next = index <= 0 ? items.length - 1 : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = items.length - 1;
    if (next === null) return;
    event.preventDefault();
    items[next]?.focus();
  };

  // One "Organization" list: the rep's own orgs, then (OrgsAdmin only) any
  // cobrand preview no org already covers. Shown only when there is a choice.
  const orgChoices: OrgChoice[] = [
    ...status.organizations.map((org) => ({
      key: `org:${org.id}`,
      name: org.name,
      branding: org.branding,
      organizationId: org.id,
      cobrand: null,
      selected: org.id === status.activeOrganizationId,
    })),
    ...status.cobrandOptions
      .filter((option) => !status.organizations.some((org) => org.branding.cobrand === option.cobrand))
      .map((option) => ({
        key: `cobrand:${option.cobrand}`,
        name: option.appName,
        branding: option,
        organizationId: null,
        cobrand: option.cobrand,
        selected: status.activeOrganizationId === null && option.cobrand === status.branding.cobrand,
      })),
  ];
  const orgName = activeOrgName(status);

  return (
    <div className={`account-menu account-menu-${variant}`} ref={root}>
      {variant === 'rail' ? (
        <button
          ref={trigger}
          type="button"
          className="sidebar-rail-avatar"
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          title="Account"
          onClick={toggle}
        >
          {initialsFromUsername(username)}
        </button>
      ) : (
        <button
          ref={trigger}
          type="button"
          className="sidebar-account"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={toggle}
        >
          <span className="sidebar-account-avatar" aria-hidden="true">
            {initialsFromUsername(username)}
          </span>
          <span className="sidebar-account-body">
            <span className="sidebar-account-name">{displayNameFromUsername(username)}</span>
            <span className="sidebar-account-meta">{orgName}</span>
          </span>
          <span className="sidebar-account-chevron account-menu-chevron" aria-hidden="true">
            <ChevronDownIcon />
          </span>
        </button>
      )}

      {open ? (
        <div id={menuId} ref={panel} className="account-menu-panel" style={variant === 'rail' ? railAnchor : undefined} role="menu" aria-label="Account" onKeyDown={onMenuKeyDown}>
          <div className="account-menu-who">
            <span className="account-menu-who-name">{displayNameFromUsername(username)}</span>
            <span className="account-menu-who-email">{username}</span>
          </div>

          {orgChoices.length > 1 ? (
            <div className="account-menu-group" role="group" aria-label="Organization">
              <span className="account-menu-label">Organization</span>
              <div className="account-menu-brands">
                {orgChoices.map((choice) => (
                  <button
                    key={choice.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={choice.selected}
                    className="account-menu-item account-menu-org"
                    title={choice.name}
                    disabled={auth.busy}
                    onClick={() => {
                      close(true);
                      if (!choice.selected) void auth.selectOrganization(choice.organizationId, choice.cobrand);
                    }}
                  >
                    <span className="account-menu-org-logo" aria-hidden="true">
                      <AppBrand branding={choice.branding} />
                    </span>
                    <span className="account-menu-org-name">{choice.name}</span>
                    {choice.selected ? <span className="account-menu-check" aria-hidden="true">✓</span> : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="account-menu-group">
            <button type="button" role="menuitem" className="account-menu-item" onClick={() => go('/settings')}>
              <PersonGlyph />
              <span>Settings</span>
            </button>
            {status.canManageCredits ? (
              <button type="button" role="menuitem" className="account-menu-item" onClick={() => go('/admin/credits')}>
                <CardsGlyph />
                <span>Proposal credits</span>
              </button>
            ) : null}
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={theme === 'dark'}
              className="account-menu-item"
              // Stays open: the rep sees the switch flip with the theme.
              onClick={toggleTheme}
            >
              <MoonGlyph />
              <span>Dark mode</span>
              <span className="account-menu-switch" data-on={theme === 'dark' ? '' : undefined} aria-hidden="true" />
            </button>
          </div>

          <div className="account-menu-group">
            <button
              type="button"
              role="menuitem"
              className="account-menu-item"
              disabled={auth.busy}
              onClick={() => {
                close();
                void auth.signOut();
              }}
            >
              <ExitGlyph />
              <span>Log out</span>
            </button>
          </div>
        </div>
      ) : null}

      {auth.error ? <p className="account-menu-error" role="alert">{auth.error}</p> : null}
    </div>
  );
}
